import React, { useState, useEffect } from 'react';
import { updateConsent, getCurrentConsent } from '../utils/analytics';
import { ellucianColors } from '../constants/colors';

export interface ConsentChoices {
  analytics: boolean;
  marketing: boolean;
  personalization: boolean;
  functionality: boolean;
}

export const ConsentBanner: React.FC = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [choices, setChoices] = useState<ConsentChoices>({
    analytics: false,
    marketing: false,
    personalization: false,
    functionality: true // Always true for basic site operation
  });

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const CONSENT_DURATION = 365 * 24 * 60 * 60 * 1000; // 12 months in milliseconds
    const existingConsent = getCurrentConsent();
    const now = Date.now();
    
    // Show banner if no consent exists OR if consent has expired
    if (!existingConsent || (existingConsent.timestamp && (now - new Date(existingConsent.timestamp).getTime()) > CONSENT_DURATION)) {
      setTimeout(() => {
        setShowBanner(true);
      }, 1500); // Show after 1.5 seconds for better UX
    }
  }, []);

  const handleAcceptAll = () => {
    const fullConsent: ConsentChoices = {
      analytics: true,
      marketing: true,
      personalization: true,
      functionality: true
    };
    
    updateConsent(fullConsent.analytics, fullConsent.marketing);
    
    // Verify consent was stored
    setTimeout(() => {
      const storedConsent = getCurrentConsent();
      if (storedConsent?.analytics && typeof window !== 'undefined' && (window as any).trackMarketplaceVisit) {
        (window as any).trackMarketplaceVisit();
      }
    }, 100);
    
    setShowBanner(false);
    
    // Show confirmation
    showConsentConfirmation('All cookies accepted');
  };

  const handleRejectAll = () => {
    const minimalConsent: ConsentChoices = {
      analytics: false,
      marketing: false,
      personalization: false,
      functionality: true // Keep functionality for basic site operation
    };
    
    updateConsent(minimalConsent.analytics, minimalConsent.marketing);
    setShowBanner(false);
    
    // Show confirmation
    showConsentConfirmation('Only essential cookies accepted');
  };

  const handleCustomChoice = () => {
    updateConsent(choices.analytics, choices.marketing);
    setShowBanner(false);
    
    // Show confirmation
    showConsentConfirmation('Your preferences have been saved');
  };

  const showConsentConfirmation = (message: string) => {
    // Simple toast notification
    const toast = document.createElement('div');
    toast.textContent = message;
    toast.className = 'fixed top-4 right-4 bg-green-600 text-white px-4 py-2 rounded z-50';
    document.body.appendChild(toast);
    
    setTimeout(() => {
      document.body.removeChild(toast);
    }, 3000);
  };

  if (!showBanner) {
    return null;
  }

  return (
    <>
      {/* CSS Override for Material-UI theme conflicts */}
      <style>
        {`
          :root {
            --consent-text-color: ${ellucianColors.neutral600};
            --consent-white-color: ${ellucianColors.white};
            --consent-primary-color: ${ellucianColors.primary};
            --consent-fountain-color: ${ellucianColors.fountain};
          }
          
          .consent-banner-container.consent-banner-container.consent-banner-container {
            font-family: "Poppins", "Segoe UI", "Helvetica Neue", sans-serif;
          }
          
          .consent-banner-container.consent-banner-container span,
          .consent-banner-container.consent-banner-container [class*="MuiGrid"] span,
          .consent-banner-container.consent-banner-container [class*="Grid-"] span,
          .consent-banner-container.consent-banner-container [class*="eds-ltr"] span,
          .consent-banner-container.consent-banner-container div span {
            color: var(--consent-text-color);
          }
          
          .consent-banner-container.consent-banner-container p,
          .consent-banner-container.consent-banner-container [class*="MuiGrid"] p,
          .consent-banner-container.consent-banner-container [class*="Grid-"] p,
          .consent-banner-container.consent-banner-container [class*="eds-ltr"] p,
          .consent-banner-container.consent-banner-container div p {
            color: var(--consent-text-color);
          }
          
          .consent-banner-container.consent-banner-container div,
          .consent-banner-container.consent-banner-container [class*="MuiGrid"] div,
          .consent-banner-container.consent-banner-container [class*="Grid-"] div,
          .consent-banner-container.consent-banner-container [class*="eds-ltr"] div {
            color: var(--consent-text-color);
          }
          
          div.consent-banner-container div.consent-banner-container * {
            color: var(--consent-text-color);
            font-family: "Poppins", "Segoe UI", "Helvetica Neue", sans-serif;
          }
          
          .consent-banner-container.primary-button.primary-button,
          .consent-banner-container .primary-button.primary-button,
          .consent-banner-container.primary-button.primary-button *,
          .consent-banner-container .primary-button.primary-button * {
            background-color: var(--consent-primary-color);
            color: var(--consent-white-color);
          }
          
          .consent-banner-container.fountain-button.fountain-button,
          .consent-banner-container .fountain-button.fountain-button,
          .consent-banner-container.fountain-button.fountain-button *,
          .consent-banner-container .fountain-button.fountain-button * {
            background-color: var(--consent-fountain-color);
            color: var(--consent-white-color);
          }
        `}
      </style>
      
      {/* Banner */}
      <div 
        className="consent-banner-container"
        style={{ 
          position: 'fixed', 
          bottom: 0, 
          left: 0, 
          right: 0, 
          backgroundColor: ellucianColors.white, 
          borderTop: `3px solid ${ellucianColors.primary}`,
          boxShadow: '0 -8px 32px rgba(0, 0, 0, 0.15)',
          zIndex: 9999,
          maxHeight: '90vh', 
          overflowY: 'auto',
          color: ellucianColors.neutral600,
          fontFamily: '"Poppins", "Segoe UI", "Helvetica Neue", sans-serif'
        }}
      >
        <div style={{ 
          maxWidth: '1200px', 
          margin: '0 auto', 
          padding: isMobile ? '24px 16px' : '32px 24px',
          color: ellucianColors.neutral600,
          fontFamily: '"Poppins", "Segoe UI", "Helvetica Neue", sans-serif',
          '--consent-override': 'true'
        } as React.CSSProperties}>
          
          {/* Header Section */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'flex-start', 
            gap: '24px',
            marginBottom: showDetails ? '24px' : '0',
            flexDirection: isMobile ? 'column' : 'row'
          }}>
            
            {/* Icon & Content */}
            <section style={{ flex: 1 }} aria-labelledby="consent-banner-title">
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  backgroundColor: ellucianColors.primary,
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '24px'
                }}>
                  🍪
                </div>
                <div>
                  <h3 id="consent-banner-title" style={{ 
                    margin: 0, 
                    fontSize: '24px', 
                    fontWeight: '600', 
                    color: ellucianColors.neutral600,
                    fontFamily: '"Poppins", "Segoe UI", "Helvetica Neue", sans-serif'
                  }}>
                    Your privacy is important to us
                  </h3>
                  <p style={{ 
                    margin: '4px 0 0 0', 
                    fontSize: '14px', 
                    color: ellucianColors.neutral600,
                    fontFamily: '"Poppins", "Segoe UI", "Helvetica Neue", sans-serif',
                    '--consent-override': 'true'
                  } as React.CSSProperties}>
                    Ellucian Marketplace
                  </p>
                </div>
              </div>
              
              <p style={{ 
                fontSize: '16px', 
                lineHeight: '1.5', 
                color: ellucianColors.neutral600,
                margin: '0 0 24px 0',
                fontFamily: '"Poppins", "Segoe UI", "Helvetica Neue", sans-serif',
                '--consent-override': 'true'
              } as React.CSSProperties}>
                Ellucian Marketplace uses anonymous data provided by cookies 
                to improve site navigation, analyze site usage, and assist in our marketing efforts.
              </p>
            </section>

            {/* Action Buttons */}
            <nav 
              style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: '12px', 
                minWidth: isMobile ? '100%' : '280px',
                alignItems: 'stretch',
                marginTop: '8px'
              }}
              aria-label="Cookie consent actions"
            >
              <button
                className="primary-button"
                onClick={handleAcceptAll}
                style={{
                  backgroundColor: ellucianColors.primary,
                  color: ellucianColors.white,
                  border: 'none',
                  padding: '14px 28px',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  fontFamily: '"Poppins", "Segoe UI", "Helvetica Neue", sans-serif',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 2px 8px rgba(113, 0, 235, 0.2)',
                  width: '100%'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = ellucianColors.secondary;
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = ellucianColors.primary;
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                Accept All Cookies
              </button>
              
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'space-between' }}>
                <button
                  onClick={() => setShowDetails(!showDetails)}
                  style={{
                    backgroundColor: 'transparent',
                    color: ellucianColors.primary,
                    border: `1px solid ${ellucianColors.mediumGray}`,
                    padding: '10px 16px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    fontFamily: '"Poppins", "Segoe UI", "Helvetica Neue", sans-serif',
                    transition: 'all 0.2s ease',
                    flex: '1'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = ellucianColors.lightGray;
                    e.currentTarget.style.borderColor = ellucianColors.primary;
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.borderColor = ellucianColors.mediumGray;
                  }}
                >
                  {showDetails ? 'Hide Options' : 'Customize'}
                </button>
                
                <button
                  onClick={handleRejectAll}
                  style={{
                    backgroundColor: 'transparent',
                    color: ellucianColors.neutral600,
                    border: `1px solid ${ellucianColors.mediumGray}`,
                    padding: '10px 16px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    fontFamily: '"Poppins", "Segoe UI", "Helvetica Neue", sans-serif',
                    transition: 'all 0.2s ease',
                    flex: '1'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = ellucianColors.lightGray;
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  Essential Only
                </button>
              </div>
            </nav>
          </div>

          {/* Detailed Preferences */}
          {showDetails && (
            <section 
              style={{ 
                marginTop: '24px',
                padding: isMobile ? '16px' : '24px',
                backgroundColor: ellucianColors.lightGray,
                borderRadius: '12px',
                border: `1px solid ${ellucianColors.mediumGray}`,
                maxHeight: '60vh',
                overflowY: 'auto'
              }}
              aria-labelledby="cookie-preferences-title"
            >
              <h4 id="cookie-preferences-title" style={{ 
                fontSize: '18px', 
                fontWeight: '600', 
                color: ellucianColors.neutral600,
                margin: '0 0 20px 0',
                fontFamily: '"Poppins", "Segoe UI", "Helvetica Neue", sans-serif'
              }}>
                Cookie Preferences
              </h4>
              
              <div style={{ display: 'grid', gap: '20px' }}>
                {/* Essential Cookies */}
                <div style={{ 
                  backgroundColor: ellucianColors.white,
                  padding: '20px',
                  borderRadius: '8px',
                  border: `1px solid ${ellucianColors.mediumGray}`
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                    <div>
                      <h5 style={{ 
                        fontSize: '16px', 
                        fontWeight: '600', 
                        color: ellucianColors.neutral600,
                        margin: '0 0 4px 0',
                        fontFamily: '"Poppins", "Segoe UI", "Helvetica Neue", sans-serif'
                      }}>
                        Essential Cookies
                      </h5>
                      <p style={{ 
                        fontSize: '14px', 
                        color: ellucianColors.neutral600, // Changed for better contrast
                        margin: 0,
                        lineHeight: '1.4'
                      }}>
                        Required for the marketplace to function properly
                      </p>
                    </div>
                    <div style={{
                      backgroundColor: ellucianColors.mediumGray,
                      borderRadius: '20px',
                      padding: '6px 12px',
                      fontSize: '12px',
                      color: ellucianColors.neutral600,
                      fontWeight: '500',
                      fontFamily: '"Poppins", "Segoe UI", "Helvetica Neue", sans-serif'
                    }}>
                      Always Active
                    </div>
                  </div>
                  <p style={{ 
                    fontSize: '13px', 
                    color: ellucianColors.neutral600, // Changed for better contrast
                    margin: 0,
                    lineHeight: '1.4'
                  }}>
                    Authentication, security, basic functionality
                  </p>
                </div>

                {/* Analytics Cookies */}
                <div style={{ 
                  backgroundColor: ellucianColors.white,
                  padding: '20px',
                  borderRadius: '8px',
                  border: `1px solid ${ellucianColors.mediumGray}`
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                    <div>
                      <h5 style={{ 
                        fontSize: '16px', 
                        fontWeight: '600', 
                        color: ellucianColors.neutral600,
                        margin: '0 0 4px 0',
                        fontFamily: '"Poppins", "Segoe UI", "Helvetica Neue", sans-serif'
                      }}>
                        Analytics Cookies
                      </h5>
                      <p style={{ 
                        fontSize: '14px', 
                        color: ellucianColors.neutral600, // Changed for better contrast
                        margin: 0,
                        lineHeight: '1.4'
                      }}>
                        Help us understand marketplace usage and popular solutions
                      </p>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <input
                        type="checkbox"
                        id="analytics"
                        checked={choices.analytics}
                        onChange={(e) => setChoices(prev => ({ ...prev, analytics: e.target.checked }))}
                        style={{
                          width: '20px',
                          height: '20px',
                          accentColor: ellucianColors.primary,
                          cursor: 'pointer'
                        }}
                      />
                    </div>
                  </div>
                  <p style={{ 
                    fontSize: '13px', 
                    color: ellucianColors.neutral600, // Changed for better contrast
                    margin: 0,
                    lineHeight: '1.4'
                  }}>
                    Google Analytics, solution view tracking, usage statistics
                  </p>
                </div>

                {/* Marketing Cookies */}
                <div style={{ 
                  backgroundColor: ellucianColors.white,
                  padding: '20px',
                  borderRadius: '8px',
                  border: `1px solid ${ellucianColors.mediumGray}`
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                    <div>
                      <h5 style={{ 
                        fontSize: '16px', 
                        fontWeight: '600', 
                        color: ellucianColors.neutral600,
                        margin: '0 0 4px 0',
                        fontFamily: '"Poppins", "Segoe UI", "Helvetica Neue", sans-serif'
                      }}>
                        Marketing Cookies
                      </h5>
                      <p style={{ 
                        fontSize: '14px', 
                        color: ellucianColors.neutral600, // Changed for better contrast
                        margin: 0,
                        lineHeight: '1.4'
                      }}>
                        Show you relevant solution recommendations
                      </p>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <input
                        type="checkbox"
                        id="marketing"
                        checked={choices.marketing}
                        onChange={(e) => setChoices(prev => ({ ...prev, marketing: e.target.checked }))}
                        style={{
                          width: '20px',
                          height: '20px',
                          accentColor: ellucianColors.primary,
                          cursor: 'pointer'
                        }}
                      />
                    </div>
                  </div>
                  <p style={{ 
                    fontSize: '13px', 
                    color: ellucianColors.neutral600, // Changed for better contrast
                    margin: 0,
                    lineHeight: '1.4'
                  }}>
                    Personalized content, targeted solutions, campaign tracking
                  </p>
                </div>

                {/* Personalization Cookies */}
                <div style={{ 
                  backgroundColor: ellucianColors.white,
                  padding: '20px',
                  borderRadius: '8px',
                  border: `1px solid ${ellucianColors.mediumGray}`
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                    <div>
                      <h5 style={{ 
                        fontSize: '16px', 
                        fontWeight: '600', 
                        color: ellucianColors.neutral600,
                        margin: '0 0 4px 0',
                        fontFamily: '"Poppins", "Segoe UI", "Helvetica Neue", sans-serif'
                      }}>
                        Personalization
                      </h5>
                      <p style={{ 
                        fontSize: '14px', 
                        color: ellucianColors.neutral600, // Changed for better contrast
                        margin: 0,
                        lineHeight: '1.4'
                      }}>
                        Remember your preferences and settings
                      </p>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <input
                        type="checkbox"
                        id="personalization"
                        checked={choices.personalization}
                        onChange={(e) => setChoices(prev => ({ ...prev, personalization: e.target.checked }))}
                        style={{
                          width: '20px',
                          height: '20px',
                          accentColor: ellucianColors.primary,
                          cursor: 'pointer'
                        }}
                      />
                    </div>
                  </div>
                  <p style={{ 
                    fontSize: '13px', 
                    color: ellucianColors.neutral600, // Changed for better contrast
                    margin: 0,
                    lineHeight: '1.4'
                  }}>
                    User preferences, saved filters, customized experience
                  </p>
                </div>
              </div>

              {/* Save Preferences Button */}
              <div style={{ marginTop: '24px', textAlign: 'center' }}>
                <button
                  className="fountain-button"
                  onClick={handleCustomChoice}
                  style={{
                    backgroundColor: ellucianColors.fountain,
                    color: ellucianColors.white,
                    border: 'none',
                    padding: '14px 32px',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    fontFamily: '"Poppins", "Segoe UI", "Helvetica Neue", sans-serif',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 2px 8px rgba(80, 227, 194, 0.3)'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = '#3DD5B0';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = ellucianColors.fountain;
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  Save My Preferences
                </button>
              </div>
            </section>
          )}
        </div>
      </div>
    </>
  );
};